import { isDeviceId } from './device-info';

/**
 * Backup copy of the backend-issued device id, kept in a first-party cookie.
 *
 * localStorage is the primary store, but two error paths in this app wipe it wholesale with
 * `localStorage.clear()` (`src/app/dashbaord/signup/signup.component.ts`,
 * `src/app/shared/services/web3-auth.service.ts`). The device is still the same device after
 * that, so DeviceTrackingService restores the id from this cookie instead of registering a new one.
 *
 * "Clear browsing data" removes cookies and localStorage together, and a private window starts
 * with neither, so both still produce a new device, which is what the backend expects.
 *
 * Host-only (no `Domain` attribute): the cookie never reaches the API host, which reads the id
 * from the `x-device-id` header only.
 */
const COOKIE_NAME = 'aoc_device_id';
const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60;

/**
 * Reads the backup device id.
 * @returns the stored device id, or null when there is none or cookies are unavailable
 */
export function readDeviceIdCookie(): string | null {
  try {
    if (typeof document === 'undefined') return null;
    const prefix = `${COOKIE_NAME}=`;
    const entry = document.cookie
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith(prefix));
    const value = entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
    return isDeviceId(value) ? value : null;
  } catch {
    // Cookies blocked by the browser: localStorage still holds the id.
    return null;
  }
}

/**
 * Stores the backup device id. Ignores anything that is not a device id.
 * @param deviceId the backend-issued device id
 */
export function writeDeviceIdCookie(deviceId: string): void {
  try {
    if (typeof document === 'undefined' || !isDeviceId(deviceId)) return;
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${deviceId}; Max-Age=${ONE_YEAR_IN_SECONDS}; Path=/; SameSite=Strict${secure}`;
  } catch {
    // Cookies blocked by the browser: localStorage still holds the id.
  }
}
